import { BaseApiClient } from '../client'

// Types — will be expanded as we build each journey
export type SocialListeningConfig = {
  id: string
  merchantId: string
  hashtagsToTrack: string[]
  platforms: string[]
  isActive: boolean
  syncIntervalHours: number
  commentModerationEnabled: boolean
  lastSyncAt: string | null
  createdAt: string
  updatedAt: string
}

export type IgConnectionStatus = {
  connected: boolean
  username: string | null
  expiresAt: string | null
}

export type BrandMention = {
  id: string
  merchantId: string
  creatorId: string | null
  platform: string
  externalPostId: string
  authorUsername: string
  authorId: string | null
  authorFollowers: number | null
  authorProfileUrl: string | null
  caption: string | null
  postUrl: string | null
  mediaType: 'image' | 'video' | 'carousel' | null
  mediaUrl: string | null
  thumbnailUrl: string | null
  r2MediaKey: string | null
  r2ThumbnailKey: string | null
  carouselMedia: { cdnUrl: string; r2Key: string | null }[] | null
  mediaWidth: number | null
  mediaHeight: number | null
  likes: number
  comments: number
  views: number
  detectedBySource: string
  matchedHashtag: string | null
  status: 'new' | 'reviewed' | 'linked' | 'ignored'
  postedAt: string | null
  createdAt: string
}

export type SocialListeningJob = {
  id: string
  merchantId: string
  trigger: 'cron' | 'manual'
  status: 'pending' | 'running' | 'completed' | 'failed'
  platform: string
  mentionsFound: number
  newMentions: number
  mediaDownloaded: number
  errorMessage: string | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
}

export type MentionSortBy = 'postedAt_desc' | 'postedAt_asc' | 'likes_desc' | 'comments_desc'

export type MentionFilters = {
  page?: number
  limit?: number
  hashtag?: string
  status?: string
  platform?: string
  mediaType?: string
  startDate?: string
  endDate?: string
  sortBy?: MentionSortBy
}

// ── Comment Moderation ──────────────────────────

export type CommentModeration = {
  id: string
  merchantId: string
  igMediaId: string
  igCommentId: string
  authorUsername: string | null
  commentText: string | null
  sentiment: 'positive' | 'negative' | 'neutral' | null
  sentimentScore: number | null
  sentimentReason: string | null
  intent: 'complaint' | 'spam' | 'question' | 'positive' | 'neutral' | null
  action: 'hidden' | 'none' | 'failed_to_hide' | 'unhidden'
  discordNotified: boolean
  postPermalink: string | null
  replyText: string | null
  repliedAt: string | null
  replyIgCommentId: string | null
  createdAt: string
  processedAt: string | null
}

export type ModerationFilters = {
  page?: number
  limit?: number
  sentiment?: string
  action?: string
  intent?: string
  startDate?: string
  endDate?: string
}

export class SocialListeningApi extends BaseApiClient {
  async getConfig() {
    return this.request<SocialListeningConfig>('/api/admin/social-listening/config')
  }

  async upsertConfig(
    data: Partial<
      Pick<
        SocialListeningConfig,
        'hashtagsToTrack' | 'syncIntervalHours' | 'isActive' | 'commentModerationEnabled'
      >
    >
  ) {
    return this.request<SocialListeningConfig>('/api/admin/social-listening/config', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getIgStatus() {
    return this.request<IgConnectionStatus>('/api/admin/social-listening/ig-status')
  }

  async getIgAuthUrl() {
    return this.request<{ authUrl: string }>('/api/admin/social-listening/ig-auth-url')
  }

  async disconnectIg() {
    return this.request<{ success: boolean; message: string }>(
      '/api/admin/social-listening/ig-disconnect',
      {
        method: 'DELETE',
      }
    )
  }

  async subscribeWebhooks() {
    return this.request<{ success: boolean; message: string }>(
      '/api/admin/social-listening/ig-subscribe-webhooks',
      { method: 'POST' }
    )
  }

  async resolveUnknownAuthors() {
    return this.request<{ resolved: number; failed: number }>(
      '/api/admin/social-listening/ig-resolve-unknown-authors',
      { method: 'POST' }
    )
  }

  async listMentions(filters: MentionFilters = {}) {
    const params = new URLSearchParams()
    if (filters.page) params.set('page', filters.page.toString())
    if (filters.limit) params.set('limit', filters.limit.toString())
    if (filters.hashtag) params.set('hashtag', filters.hashtag)
    if (filters.status) params.set('status', filters.status)
    if (filters.platform) params.set('platform', filters.platform)
    if (filters.mediaType) params.set('mediaType', filters.mediaType)
    if (filters.startDate) params.set('startDate', filters.startDate)
    if (filters.endDate) params.set('endDate', filters.endDate)
    if (filters.sortBy) params.set('sortBy', filters.sortBy)
    const query = params.toString()
    return this.requestPaginated<BrandMention>(
      `/api/admin/social-listening/mentions${query ? `?${query}` : ''}`
    )
  }

  async updateMentionStatus(id: string, status: BrandMention['status']) {
    return this.request<BrandMention>(`/api/admin/social-listening/mentions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
  }

  async linkMentionToCreator(id: string, creatorId: string) {
    return this.request<BrandMention>(`/api/admin/social-listening/mentions/${id}/link`, {
      method: 'POST',
      body: JSON.stringify({ creatorId }),
    })
  }

  async getMentionMediaUrl(id: string) {
    return this.request<{ url: string }>(`/api/admin/social-listening/mentions/${id}/media`)
  }

  async triggerSync() {
    return this.request<{ jobId: string }>('/api/admin/social-listening/sync', {
      method: 'POST',
    })
  }

  async enrichProfiles() {
    return this.request<{ enriched: number; skipped: number }>(
      '/api/admin/social-listening/enrich-profiles',
      {
        method: 'POST',
      }
    )
  }

  async apifySync() {
    return this.request<{ totalScraped: number; newCount: number }>(
      '/api/admin/social-listening/apify-sync',
      { method: 'POST' }
    )
  }

  async listJobs() {
    return this.request<SocialListeningJob[]>('/api/admin/social-listening/jobs')
  }

  // ── Comment Moderation ──────────────────────────

  async listModerations(filters: ModerationFilters = {}) {
    const params = new URLSearchParams()
    if (filters.page) params.set('page', filters.page.toString())
    if (filters.limit) params.set('limit', filters.limit.toString())
    if (filters.sentiment) params.set('sentiment', filters.sentiment)
    if (filters.action) params.set('action', filters.action)
    if (filters.intent) params.set('intent', filters.intent)
    if (filters.startDate) params.set('startDate', filters.startDate)
    if (filters.endDate) params.set('endDate', filters.endDate)
    const query = params.toString()
    return this.requestPaginated<CommentModeration>(
      `/api/admin/social-listening/moderations${query ? `?${query}` : ''}`
    )
  }

  async hideComment(id: string) {
    return this.request<CommentModeration>(`/api/admin/social-listening/moderations/${id}/hide`, {
      method: 'POST',
    })
  }

  async unhideComment(id: string) {
    return this.request<CommentModeration>(`/api/admin/social-listening/moderations/${id}/unhide`, {
      method: 'POST',
    })
  }

  async replyToComment(id: string, data: { message: string; unhide?: boolean }) {
    return this.request<CommentModeration>(`/api/admin/social-listening/moderations/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async suggestReply(id: string) {
    return this.request<{ suggestion: string }>(
      `/api/admin/social-listening/moderations/${id}/suggest-reply`,
      {
        method: 'POST',
      }
    )
  }
}
